import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Grid,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageLayout from '../../components/layout/PageLayout';
import {
  useGetLeadQuizByIdQuery,
  useCreateLeadQuizMutation,
  useUpdateLeadQuizMutation,
  useLazyGetStatusesByModelQuery,
  useLazyGetStatusesQuery,
} from '../../store/api';
import { extractList } from '../../utils/response';
import { useLazySelectOptions } from '../../hooks/useLazySelectOptions';
import { useServerValidationErrors } from '../../hooks/useServerValidationErrors';
import { toast } from 'react-toastify';
import { buildLeadQuizRelationParams } from './leadQuizApiHelpers';

const buildFormData = (values) => {
  const fd = new FormData();
  fd.append('status_id', values.status_id);
  fd.append('question', values.question ?? '');
  return fd;
};

const statusLabel = (s) => s.label ?? s.name ?? s.title ?? String(s.id);

const CreateLeadQuiz = () => {
  const { id: editId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(editId);

  const [selectedStatus, setSelectedStatus] = useState(null);
  const [question, setQuestion] = useState('');
  const [hydrated, setHydrated] = useState(false);
  const { setErrors, applyApiError, clearError, fieldProps } =
    useServerValidationErrors();

  const [fetchStatusesByModel] = useLazyGetStatusesByModelQuery();
  const [fetchStatuses] = useLazyGetStatusesQuery();

  const leadQuizStatusTrigger = useCallback(
    () => ({
      unwrap: async () => {
        try {
          return await fetchStatusesByModel('leadQuiz').unwrap();
        } catch {
          return await fetchStatuses({ paginate: 0 }).unwrap();
        }
      },
    }),
    [fetchStatusesByModel, fetchStatuses]
  );

  const statusSelect = useLazySelectOptions(leadQuizStatusTrigger, {
    mapResult: (res) => {
      const list = extractList(res);
      const arr = Array.isArray(list) ? list : [];
      return arr.map((s) => ({
        id: s.id,
        label: statusLabel(s),
        raw: s,
      }));
    },
  });

  const loadStatuses = useCallback(
    () =>
      statusSelect.load().catch(() => {
        toast.error('Could not load statuses');
      }),
    [statusSelect]
  );

  const {
    data: quizData,
    isLoading: quizLoading,
    isError: quizError,
    error: quizErr,
  } = useGetLeadQuizByIdQuery(
    { id: editId, params: buildLeadQuizRelationParams() },
    { skip: !isEdit || !editId }
  );
  const [createLeadQuiz, { isLoading: creating }] = useCreateLeadQuizMutation();
  const [updateLeadQuiz, { isLoading: updating }] = useUpdateLeadQuizMutation();
  const submitting = creating || updating;
  const loadingPage = isEdit && (quizLoading || !hydrated);

  useEffect(() => {
    if (quizError) {
      toast.error(quizErr?.data?.message || 'Failed to load lead quiz');
      navigate('/lead-quizzes');
    }
  }, [quizError, quizErr, navigate]);

  useEffect(() => {
    if (!isEdit || !quizData || hydrated) return;
    const q = quizData?.data ?? quizData;
    if (!q) return;
    setQuestion(q.question ?? '');

    const nestedStatus = q.status && typeof q.status === 'object' ? q.status : null;
    const sid = q.status_id ?? nestedStatus?.id ?? undefined;

    if (sid) {
      if (nestedStatus) {
        const opt = { id: sid, label: statusLabel(nestedStatus), raw: nestedStatus };
        setSelectedStatus(opt);
        statusSelect.setOptions((prev) => (prev.some((o) => o.id === sid) ? prev : [...prev, opt]));
      } else {
        const opt = statusSelect.options.find((o) => o.id === sid);
        setSelectedStatus(opt ?? { id: sid, label: String(sid), raw: {} });
      }
    } else {
      setSelectedStatus(null);
    }
    setHydrated(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional dependency list
  }, [isEdit, quizData, statusSelect.options, hydrated]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const clientErrors = {};
    if (!question.trim()) clientErrors.question = 'Question is required';
    if (!selectedStatus?.id) clientErrors.status_id = 'Status is required';
    setErrors(clientErrors);
    if (Object.keys(clientErrors).length > 0) return;

    const values = {
      status_id: selectedStatus.id,
      question: question.trim(),
    };
    const fd = buildFormData(values);

    try {
      // No relations on write requests — the page navigates away from the response
      if (isEdit) {
        await updateLeadQuiz({ id: editId, data: fd }).unwrap();
        toast.success('Lead quiz updated');
      } else {
        await createLeadQuiz(fd).unwrap();
        toast.success('Lead quiz added');
      }
      navigate('/lead-quizzes');
    } catch (err) {
      applyApiError(
        err,
        isEdit ? 'Failed to update lead quiz' : 'Failed to add lead quiz'
      );
    }
  };

  if (loadingPage) {
    return (
      <PageLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/lead-quizzes')} sx={{ mb: 2 }}>
          Back to lead quizzes
        </Button>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
          {isEdit ? 'Edit lead quiz' : 'Add lead quiz'}
        </Typography>

        <Card>
          <CardContent component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Question"
                  value={question}
                  onChange={(e) => {
                    setQuestion(e.target.value);
                    clearError('question');
                  }}
                  required
                  fullWidth
                  multiline
                  minRows={2}
                  {...fieldProps('question')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={statusSelect.options}
                  value={selectedStatus}
                  onChange={(_, v) => {
                    setSelectedStatus(v);
                    clearError('status_id');
                  }}
                  onOpen={loadStatuses}
                  loading={statusSelect.loading}
                  getOptionLabel={(opt) => (opt?.label != null ? opt.label : '')}
                  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Status"
                      required
                      fullWidth
                      {...fieldProps('status_id')}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Button type="submit" variant="contained" disabled={submitting}>
                  {submitting ? <CircularProgress size={22} /> : isEdit ? 'Save changes' : 'Add lead quiz'}
                </Button>
                <Button type="button" variant="outlined" onClick={() => navigate('/lead-quizzes')} disabled={submitting}>
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default CreateLeadQuiz;
